import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { secondsToMilliseconds } from 'date-fns';
import { Image } from 'expo-image';
import type { ImageSource } from 'expo-image';
import React from 'react';
import { twMerge } from 'tailwind-merge';

import { promotional_chat, promotional_network, promotional_testimonials } from '@/assets/images';
import { Heading } from '@/components/heading';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';

/**
 * Individual promotional slide component displaying image, title, and description.
 * Renders with fixed image dimensions and responsive text styling based on dark mode.
 * Images use contain fit to ensure full artwork visibility without cropping.
 *
 * Also exports the promotional slides data and pagination dot size constant.
 */

export type PromotionalCarouselSlideData = {
  imageSrc: ImageSource;
  imageAlt: string;
  title: string;
  description: string;
};

export const PROMOTIONAL_SLIDES: PromotionalCarouselSlideData[] = [
  {
    imageSrc: promotional_network,
    imageAlt: 'network of experts graphic',
    title: 'Expand your support network',
    description:
      "1-1 consultations with 100+ of the top oncologists in the country. It's mentorship you can trust — completely free",
  },
  {
    imageSrc: promotional_chat,
    imageAlt: 'chat bubble graphic',
    title: 'Ask any question, guaranteed response',
    description:
      'Ask for advice about patient cases, new data, clinical trials and treatments with an average response time of ~2 hours.',
  },
  {
    imageSrc: promotional_testimonials,
    imageAlt: 'testimonials graphic',
    title: 'What your peers are saying',
    description:
      'With over 5,000 inquiries completed, we are a trusted as a free resource to get guaranteed responses',
  },
];

export const DOT_SIZE = 8;

export const SLIDE_INTERVAL_MS = secondsToMilliseconds(7);

/**
 * Multiplier for calculating image width as a percentage of content width.
 * Images will be 80% of the available content width.
 */
export const IMAGE_WIDTH_MULTIPLIER = 0.8;

/**
 * Aspect ratio multiplier for images on web platforms.
 * Creates a slightly compressed aspect ratio (0.85) for better visual balance on desktop.
 */
export const IMAGE_ASPECT_MULTIPLIER_WEB = 0.85;

/**
 * Aspect ratio multiplier for images on mobile platforms.
 * Maintains original aspect ratio (1.0) for optimal display on mobile devices.
 */
export const IMAGE_ASPECT_MULTIPLIER_MOBILE = 1;

export type PromotionalCarouselSlideProps = {
  width: number;
  onDark?: boolean;
} & PromotionalCarouselSlideData;

export function PromotionalCarouselSlide({
  width,
  imageAlt,
  imageSrc,
  title,
  description,
  onDark = false,
}: PromotionalCarouselSlideProps) {
  const contentWidth = Math.max(0, Math.floor(width));

  const imageWidth = Math.floor(contentWidth * IMAGE_WIDTH_MULTIPLIER);

  const imageAspectMultiplier = isWeb
    ? IMAGE_ASPECT_MULTIPLIER_WEB
    : IMAGE_ASPECT_MULTIPLIER_MOBILE;

  const imageHeight = Math.max(220, Math.floor(imageWidth * imageAspectMultiplier));

  return (
    <VStack className="items-center justify-center gap-0" style={{ width }}>
      <Image
        alt={imageAlt}
        source={imageSrc}
        contentFit="contain"
        style={{ width: imageWidth, height: imageHeight }}
      />
      <VStack className="w-full px-6 web:px-16">
        <Heading
          size={isWeb ? 'h2-2xl' : 'h2-lg'}
          className={twMerge(
            'mb-2 text-center font-body font-[600] leading-normal web:-mt-10',
            onDark ? 'text-typography-0' : 'text-typography-900'
          )}>
          {title}
        </Heading>
        <Text
          size="md"
          className={twMerge(
            'w-full text-center font-light leading-normal',
            onDark ? 'text-typography-0' : 'text-typography-700'
          )}>
          {description}
        </Text>
      </VStack>
    </VStack>
  );
}
